import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

interface Volunteer {
  id: number;
  first_name: string;
  last_name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  emergency_contact: string | null;
  skills: string | null;
  interests: string | null;
  is_active: number;
}

interface VolunteerFormProps {
  volunteer?: Volunteer | null;
  onClose: () => void;
}

export function VolunteerForm({ volunteer, onClose }: VolunteerFormProps) {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [skills, setSkills] = useState("");
  const [interests, setInterests] = useState("");

  useEffect(() => {
    if (volunteer) {
      setFirstName(volunteer.first_name);
      setLastName(volunteer.last_name);
      setPhone(volunteer.phone ?? "");
      setEmail(volunteer.email ?? "");
      setSkills(volunteer.skills ?? "");
      setInterests(volunteer.interests ?? "");
    }
  }, [volunteer]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post("/volunteers", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: Record<string, unknown>) =>
      api.put(`/volunteers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data = {
      firstName,
      lastName,
      phone: phone || undefined,
      email: email || undefined,
      skills: skills || undefined,
      interests: interests || undefined,
    };

    if (volunteer) {
      updateMutation.mutate({ id: volunteer.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error ?? updateMutation.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {volunteer ? "Edit Volunteer" : "New Volunteer"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="First name"
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder="Last name"
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
            />
            <Input
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-5555"
            />
            <Input
              label="Skills"
              value={skills}
              onChange={(e) => setSkills(e.target.value)}
              placeholder="e.g. translation, counseling"
            />
            <Input
              label="Interests"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              placeholder="e.g. outreach, events"
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" loading={isPending}>
              {volunteer ? "Save Changes" : "Create Volunteer"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
